import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { expensesApi, ledgerApi, projectsApi, salesApi, vendorsApi, unwrap } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Button, Empty, Field, Icon, Input, Loader } from "../components/ui";
import { ProofLine } from "../components/PaymentFields";
import { InstallmentPlanTable } from "../components/InstallmentPlan";
import { paymentLabel } from "../utils/finance";
import { formatDate, formatMoney, formatMonth } from "../utils/format";
import { downloadStatementPdf } from "../utils/statementPdf";

const loaders = {
  vendor: (id, query) => vendorsApi.statement(id, query),
  expense: (id, query) => expensesApi.statement(id, query),
  project: (id, query) => projectsApi.statement(id, query),
  sale: (id, query) => salesApi.statement(id, query),
  ledger: (_id, query) => ledgerApi.statement(query),
  ledgerDay: (id, query) => ledgerApi.dailyStatement(id, query),
};

const backTo = {
  vendor: (id) => `/vendors/${id}`,
  expense: () => "/expenses",
  project: (id) => `/projects/${id}`,
  sale: (id) => `/sales/${id}`,
  ledger: () => "/ledger",
  ledgerDay: () => "/ledger",
};

const kindLabel = {
  vendor: "Vendor",
  expense: "Expense",
  project: "Project",
  sale: "Sale",
  ledger: "Cash book",
  ledgerDay: "Daily book",
};

const partyTitle = (kind, data) =>
  data.sale?.buyerName ||
  data.vendor?.name ||
  data.expense?.title ||
  data.project?.name ||
  (kind === "ledgerDay" && data.from ? formatDate(data.from) : "") ||
  (data.scope === "month" && data.from ? formatMonth(String(data.from).slice(0, 7)) : "") ||
  "Cash book";

const partySubtitle = (data) =>
  [
    data.sale?.propertyName,
    data.sale?.project?.name,
    data.vendor?.phone,
    data.project?.location,
    data.expense?.vendor?.name,
  ]
    .filter(Boolean)
    .join(" · ");

export default function Statement({ kind }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { query } = useBuilderQuery();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [month, setMonth] = useState(params.get("month") || "");

  const statementQuery = () => {
    if (kind === "ledgerDay") return { ...query };
    if (kind === "ledger" && month) return { ...query, month };
    return { ...query, from, to };
  };

  const load = async () => {
    setLoading(true);
    try {
      setData(unwrap(await loaders[kind](id, statementQuery())));
    } catch (error) {
      toast.error(error.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, kind, query.builderId]);

  if (loading) return <Loader />;
  if (!data) return <Empty title="Statement not found" text="Go back and try again." />;

  const title = partyTitle(kind, data);
  const subtitle = partySubtitle(data);
  const totals = data.totals || {};
  const rows = data.rows || [];
  const start = from || data.from;
  const end = to || data.to;
  const period =
    month && kind === "ledger"
      ? formatMonth(month)
      : start || end
        ? `${start ? formatDate(start) : "Start"} — ${end ? formatDate(end) : "Today"}`
        : "All dates";

  const printSheet = () => window.print();

  const savePdf = () => {
    try {
      downloadStatementPdf({
        title,
        subtitle,
        kind: kindLabel[kind] || kind,
        from: from || data.from,
        to: to || data.to,
        rows,
        totals,
      });
    } catch (error) {
      toast.error(error.message || "Could not make the PDF.");
    }
  };

  return (
    <div className="stack statement-page">
      <Link to={backTo[kind](id)} className="back-link no-print">
        <Icon name="back" size={18} /> Back
      </Link>

      <header className="page-head no-print">
        <div>
          <p className="eyebrow">{kindLabel[kind]} statement</p>
          <h1>{title}</h1>
          {subtitle ? <p className="muted">{subtitle}</p> : null}
        </div>
        <div className="head-actions">
          <Button variant="ghost" onClick={savePdf}>
            Download PDF
          </Button>
          <Button onClick={printSheet}>Print</Button>
        </div>
      </header>

      {kind !== "ledgerDay" ? (
        <form
          className="filters no-print"
          onSubmit={(event) => {
            event.preventDefault();
            load();
          }}
        >
          {kind === "ledger" ? (
            <Field label="Month">
              <Input
                type="month"
                value={month}
                onChange={(event) => {
                  setMonth(event.target.value);
                  setFrom("");
                  setTo("");
                }}
              />
            </Field>
          ) : null}
          <Field label="From">
            <Input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                if (event.target.value) setMonth("");
              }}
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                if (event.target.value) setMonth("");
              }}
            />
          </Field>
          <Button type="submit" variant="ghost">
            Apply
          </Button>
        </form>
      ) : null}

      <section className="statement-sheet" aria-label="Statement">
        <header className="statement-letterhead">
          <div>
            <p className="eyebrow">Builder Books</p>
            <h2>{kindLabel[kind]} statement</h2>
          </div>
          <div className="statement-issued">
            <span>Issued</span>
            <b>{formatDate(new Date().toISOString().slice(0, 10))}</b>
          </div>
        </header>

        <div className="statement-party">
          <div>
            <span>Account</span>
            <b>{title}</b>
            {subtitle ? <small>{subtitle}</small> : null}
          </div>
          <div>
            <span>Period</span>
            <b>{period}</b>
          </div>
          {totals.received != null || totals.remaining != null ? (
            <div>
              <span>Received / remaining</span>
              <b>
                {formatMoney(totals.received)} / {formatMoney(totals.remaining)}
              </b>
            </div>
          ) : null}
        </div>

        <div className="statement-totals">
          <div>
            <span>Debit</span>
            <b>{formatMoney(totals.debit)}</b>
          </div>
          <div>
            <span>Credit</span>
            <b>{formatMoney(totals.credit)}</b>
          </div>
          <div>
            <span>Balance</span>
            <b>{formatMoney(totals.balance)}</b>
          </div>
        </div>

        {rows.length === 0 ? (
          <Empty title="No rows" text="No entries in this date range." />
        ) : (
          <div className="statement-table" role="table">
            <div className="statement-head" role="row">
              <span>Date</span>
              <span>Particulars</span>
              <span>Type</span>
              <span>Debit</span>
              <span>Credit</span>
              <span>Balance</span>
            </div>
            {rows.map((row) => (
              <div
                key={`${row.kind}-${row.id}`}
                className={`statement-row ${row.affectsBalance === false ? "statement-offbook" : ""}`}
                role="row"
              >
                <span>{formatDate(row.date)}</span>
                <span>
                  {row.particulars}
                  {row.affectsBalance === false ? " · Khata only" : ""}
                  <ProofLine
                    paymentType={row.paymentType}
                    referenceNumber={row.referenceNumber}
                    image={row.image}
                    bank={row.bank}
                  />
                </span>
                <span>{paymentLabel(row.paymentType)}</span>
                <span className="num">{row.debit ? formatMoney(row.debit) : "—"}</span>
                <span className="num">{row.credit ? formatMoney(row.credit) : "—"}</span>
                <span className="num">{formatMoney(row.balance)}</span>
              </div>
            ))}
            <div className="statement-row statement-foot" role="row">
              <span />
              <span>Totals</span>
              <span />
              <span className="num">{formatMoney(totals.debit)}</span>
              <span className="num">{formatMoney(totals.credit)}</span>
              <span className="num">{formatMoney(totals.balance)}</span>
            </div>
          </div>
        )}

        {kind === "sale" && (data.plan || []).length ? (
          <div className="statement-plan">
            <h3>Installment plan</h3>
            <InstallmentPlanTable plan={data.plan} />
          </div>
        ) : null}
      </section>
    </div>
  );
}
